import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PlusIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import EventDatePicker from "../../../../components/controls/DatePicker";
import TimeSelector from "../../../../components/controls/TimeSelector";
import CancelFollowUpModal from "./popups/CancelFollowUpModal";
import CompleteFollowUpModal from "./popups/CompleteFollowUpModal";
import Autocomplete from "../../../../components/Places/PlacesAutocomplete";
import FormInput from "../../../../components/controls/FormInput";
import { formatPhoneNumber } from "../../../../globalFunctions/PhoneNumberFormatter";

const followUpTypes = {
  1: "followUps.followUpType.inPerson",
  2: "followUps.followUpType.phone",
  3: "followUps.followUpType.email",
};

const followUpStatuses = {
  1: "followUps.status.inProgress",
  2: "followUps.status.completed",
  3: "followUps.status.canceled",
};

const typeIcons = {
  1: <UserIcon className="h-4 w-4 inline-block mr-1" />,
  2: <PhoneIcon className="h-4 w-4 inline-block mr-1" />,
  3: <EnvelopeIcon className="h-4 w-4 inline-block mr-1" />,
};

const statusIcons = {
  1: <ClockIcon className="h-4 w-4 inline-block mr-1" />, // En cours
  2: <CheckCircleIcon className="h-4 w-4 inline-block mr-1" />, // Complété
  3: <XCircleIcon className="h-4 w-4 inline-block mr-1" />, // Annulé
};

const allowedTransitions = {
  1: [2, 3], // En cours → Complété ou Annulé
  2: [], // Complété → rien (ou à adapter selon ton besoin)
  3: [], // Annulé → rien
};

export default function FollowUpsTab({
  userId,
  currentUserId,
  showUserSelection = false,
}) {
  const { t } = useTranslation();
  const [followUps, setFollowUps] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newDate, setNewDate] = useState("");
  const [newType, setNewType] = useState("1");
  const [loading, setLoading] = useState(false);
  const [cancelingFollowUpId, setCancelingFollowUpId] = useState(null);
  const [completingFollowUpId, setCompletingFollowUpId] = useState(null);
  const [cancelReasonInput, setCancelReasonInput] = useState("");
  const [completedNoteInput, setCompletedNoteInput] = useState("");
  const [contactValue, setContactValue] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(userId || "");

  useEffect(() => {
    if (!showUserSelection && userId) {
      setSelectedUserId(userId);
    } else {
      fetchClientList();
      setSelectedUserId("");
    }
  }, [showUserSelection, userId]);

  const fetchClientList = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/Users`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": currentUserId, // <- ici le header attendu
          },
        }
      );

      const data = await res.json();
      setClients(data);
    } catch (err) {
      console.error("Erreur lors du chargement des clients", err);
    }
  };

  useEffect(() => {
    if (selectedUserId) {
      fetchFollowUps();
    }
  }, [selectedUserId]);

  const fetchFollowUps = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/followups/${
          selectedUserId || userId
        }`
      );
      const data = await res.json();
      setFollowUps(data);
    } catch (err) {
      console.error("Erreur lors du chargement des suivis", err);
    }
  };

  useEffect(() => {
    if (userId) fetchFollowUps();
  }, [userId]);

  const handleAddFollowUp = async () => {
    if (!newNote || !newDate || !selectedUserId || !currentUserId) return;
    setLoading(true);
    try {
      const payload = {
        userId: selectedUserId,
        createdBy: currentUserId,
        note: newNote,
        dueDate: newDate,
        followUpType: parseInt(newType),
        email: newType === "3" ? contactValue.trim() : null,
        phoneNumber: newType === "2" ? contactValue.trim() : null,
        address: newType === "1" ? contactValue.trim() : null,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/followups`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error(await res.text());

      const created = await res.json();
      setFollowUps([created, ...followUps]);
      setNewNote("");
      setNewDate("");
      setNewType("1");
      setSelectedUserId(userId || "");
      alert(t("followUps.followUpAdded"));
    } catch (err) {
      alert("Erreur: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = (followUpId, newStatus, currentStatus) => {
    const numericStatus = parseInt(newStatus);

    if (numericStatus === 3 && currentStatus !== 3) {
      setCancelingFollowUpId(followUpId);
      return;
    }

    if (numericStatus === 2 && currentStatus !== 2) {
      setCompletingFollowUpId(followUpId);
      return;
    }
    updateStatusOnServer(followUpId, numericStatus, null, null);
  };

  const updateStatusOnServer = async (
    followUpId,
    status,
    cancelReason,
    completedNote
  ) => {
    try {
      const payload = {
        status,
        cancelReason,
        completedNote,
      };

      await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/followups/${followUpId}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      fetchFollowUps();
    } catch (err) {
      alert("Erreur: " + err.message);
    } finally {
      setCancelingFollowUpId(null);
      setCompletingFollowUpId(null);
      setCancelReasonInput("");
      setCompletedNoteInput("");
    }
  };

  return (
    <div className="space-y-6 text-primary">
      {/* Nouvelle création */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold">{t("followUps.addFollowUp")}</h3>
        <button
          onClick={handleAddFollowUp}
          disabled={loading || !selectedUserId}
          className={`px-4 py-2 rounded-full inline-flex items-center transition-all
    ${
      loading || !selectedUserId
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-primary text-white hover:bg-primary/90"
    }`}
        >
          {t("global.add")}
          <PlusIcon className="h-5 w-5 ml-2 -mt-0.5" />
        </button>
      </div>

      {/* Ajout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Colonne de gauche : Note + Type */}
        <div className="space-y-4">
          {showUserSelection && (
            <div>
              <label className=" text-sm text-primary">
                {t("Client")}{" "}
                <span className="text-muted">{t("global.required")}</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={selectedUserId}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                }}
              >
                <option value="">-- Choisir un client --</option>
                {clients.map((client) => (
                  <option key={client.userId} value={client.userId}>
                    {client.fullName} - {client.email}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className=" text-sm text-primary">
              {t("Note")}{" "}
              <span className="text-muted">{t("global.required")}</span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full h-24"
              placeholder={t("followUps.notePlaceholder")}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
            />
          </div>
          <div>
            <label className=" text-sm text-primary">
              {t("followUps.typeLabel")}{" "}
              <span className="text-muted">{t("global.required")}</span>
              {/* Exemple de clé i18n : "Type de suivi" */}
            </label>
            <select
              className="select select-bordered w-full"
              value={newType}
              onChange={(e) => {
                setNewType(e.target.value);
                setContactValue(""); // On vide le champ à chaque changement de type
              }}
            >
              {Object.entries(followUpTypes).map(([id, label]) => (
                <option key={id} value={id}>
                  {t(label)}
                </option>
              ))}
            </select>
          </div>

          {newType === "3" && (
            <FormInput
              label={t("contactPage.emailLabel")}
              name="email"
              value={contactValue}
              onChange={(_, value) => setContactValue(value)}
              placeholder={t("contactPage.emailPlaceholder")}
              type="email"
              icon={<EnvelopeIcon className="w-5 h-5 text-gray-500" />}
              required
            />
          )}

          {newType === "2" && (
            <FormInput
              label={t("becomeProvider.phoneNumberLabel")}
              name="phone"
              value={contactValue}
              onChange={(_, value) => setContactValue(value)}
              placeholder={t("becomeProvider.phoneNumberPlaceholder")}
              type="tel"
              icon={<PhoneIcon className="w-5 h-5 text-gray-500" />}
              required
            />
          )}

          {newType === "1" && (
            <div>
              <Autocomplete
                onSelect={(e) => setContactValue(e.formattedAddress)}
                initialAddress=""
                showOptional={false}
                customClassName=""
                required
              />
            </div>
          )}
        </div>

        {/* Colonne de droite : Date + Heure */}
        <div className="space-y-2">
          <EventDatePicker
            label={t("ticketing.eventDateLabel")}
            value={newDate}
            onChange={(newDateOnly) => {
              if (!newDateOnly) return;
              const oldDate = newDate ? new Date(newDate) : null;
              const updated = new Date(newDateOnly);
              if (oldDate) {
                updated.setHours(oldDate.getHours());
                updated.setMinutes(oldDate.getMinutes());
              }
              setNewDate(updated);
            }}
          />

          <TimeSelector
            label={t("modal.startTime")}
            value={
              newDate
                ? `${new Date(newDate)
                    .getHours()
                    .toString()
                    .padStart(2, "0")}:${new Date(newDate)
                    .getMinutes()
                    .toString()
                    .padStart(2, "0")}`
                : ""
            }
            onChange={(time) => {
              if (!newDate || !time.includes(":")) return;
              const [h, m] = time.split(":").map(Number);
              const updated = new Date(newDate);
              updated.setHours(h);
              updated.setMinutes(m);
              setNewDate(updated);
            }}
          />
        </div>
      </div>

      <div className="divider" />
      <h3 className="text-xl font-bold">{t("followUps.followUpList")}</h3>
      {/* Liste */}
      {followUps.length === 0 ? (
        <p className="italic text-sm text-primary">
          {t("followUps.noFollowUps")}
        </p>
      ) : (
        followUps.map((f) => {
          const isOverdue = new Date(f.dueDate) < new Date() && f.status === 1;
          const allowedNextStatuses = allowedTransitions[f.status] || [];
          const isDropdownDisabled = allowedNextStatuses.length === 0;
          return (
            <div
              key={f.id}
              className={`p-3 border rounded shadow-sm space-y-1 ${
                f.status === 2
                  ? "bg-green-50"
                  : f.status === 3
                  ? "bg-red-50 text-muted"
                  : ""
              }
              ${isOverdue ? "border-red-500 bg-red-100" : "border-gray-200"}`}
            >
              <div className="flex flex-wrap items-center text-xs text-gray-500 gap-2 mb-2">
                {/* Date */}
                <span>{new Date(f.dueDate).toLocaleString()} -</span>

                {/* Type Badge */}
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full border text-primary/80 ${
                    f.followUpType === 1
                      ? "bg-pink-200"
                      : f.followUpType === 2
                      ? "bg-blue-200"
                      : "bg-yellow-200"
                  }`}
                >
                  {typeIcons[f.followUpType]}
                  {t(followUpTypes[f.followUpType])}
                </span>

                {/* Status Badge */}
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full border text-primary/80 ${
                    f.status === 2
                      ? "bg-green-300"
                      : f.status === 3
                      ? "bg-red-300"
                      : "bg-gray-200"
                  }`}
                >
                  {statusIcons[f.status]}
                  {t(followUpStatuses[f.status])}
                </span>

                {/* Overdue */}
                {isOverdue && f.status === 1 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full border bg-red-500 text-white">
                    {t("followUps.overdue")}
                  </span>
                )}

                {/* Contact Infos sous forme de badges */}
                {f.email && (
                  <a
                    href={`mailto:${f.email}`}
                    className="inline-flex items-center px-2 py-1 rounded-full border text-primary bg-blue-100 hover:bg-blue-200 transition underline"
                  >
                    <EnvelopeIcon className="h-4 w-4 mr-1" />
                    {f.email}
                  </a>
                )}

                {f.phoneNumber && (
                  <a
                    href={`tel:${f.phoneNumber}`}
                    className="inline-flex items-center px-2 py-1 rounded-full border text-primary bg-blue-100 hover:bg-green-200 transition underline"
                  >
                    <PhoneIcon className="h-4 w-4 mr-1" />
                    {formatPhoneNumber(f.phoneNumber)}
                  </a>
                )}

                {f.address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      f.address
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-2 py-1 rounded-full border text-primary bg-blue-100 hover:bg-purple-200 transition underline"
                  >
                    <UserIcon className="h-4 w-4 mr-1" />
                    {f.address}
                  </a>
                )}
              </div>

              <hr className="py-1" />

              {/* Note */}
              <div className={f.status === 1 ? "text-primary" : "text-muted"}>
                {f.note}
              </div>
              {/* Dropdown de changement de statut */}
              <div className="flex items-center space-x-2 mt-1">
                {f.status === 1 && (
                  <select
                    className="select select-xs select-bordered"
                    value={f.status}
                    onChange={(e) =>
                      handleUpdateStatus(f.id, e.target.value, f.status)
                    }
                  >
                    {Object.entries(followUpStatuses)
                      .filter(([statusKey]) => {
                        const numericKey = parseInt(statusKey);
                        return (
                          numericKey === f.status ||
                          allowedNextStatuses.includes(numericKey)
                        );
                      })
                      .map(([id, label]) => (
                        <option key={id} value={id}>
                          {t(label)}
                        </option>
                      ))}
                  </select>
                )}

                {f.status === 3 && f.cancelReason && (
                  <div className="text-sm italic text-gray-600 mt-1">
                    <span className="font-bold">
                      {t("followUps.cancellationReason")}:
                    </span>{" "}
                    {f.cancelReason}
                  </div>
                )}

                {f.status === 2 && f.completedNote && (
                  <div className="text-sm italic text-gray-600 mt-1">
                    <span className="font-bold">
                      {t("followUps.completionNote")}:
                    </span>{" "}
                    {f.completedNote}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
      <CancelFollowUpModal
        isOpen={cancelingFollowUpId !== null}
        reason={cancelReasonInput}
        onChangeReason={setCancelReasonInput}
        onClose={() => setCancelingFollowUpId(null)}
        onConfirm={(finalReason) =>
          updateStatusOnServer(cancelingFollowUpId, 3, finalReason, null)
        }
      />

      <CompleteFollowUpModal
        isOpen={completingFollowUpId !== null}
        note={completedNoteInput}
        onChangeNote={setCompletedNoteInput}
        onClose={() => setCompletingFollowUpId(null)}
        onConfirm={(finalNote) =>
          updateStatusOnServer(completingFollowUpId, 2, null, finalNote)
        }
      />
    </div>
  );
}
